import { useEffect, useState } from 'react';
import type { OpeningHours, Shop, Weekday } from '@reims-local/types';
import { WEEKDAYS } from '@reims-local/types';
import { shopApi } from '../lib/api';

const WEEKDAY_LABELS: Record<Weekday, string> = {
  mon: 'Lundi', tue: 'Mardi', wed: 'Mercredi', thu: 'Jeudi', fri: 'Vendredi', sat: 'Samedi', sun: 'Dimanche',
};

export function Settings() {
  const [shop, setShop] = useState<Shop | null>(null);
  const [hours, setHours] = useState<Record<Weekday, { enabled: boolean; open: string; close: string }>>(() => ({
    mon: { enabled: false, open: '10:00', close: '19:00' },
    tue: { enabled: false, open: '10:00', close: '19:00' },
    wed: { enabled: false, open: '10:00', close: '19:00' },
    thu: { enabled: false, open: '10:00', close: '19:00' },
    fri: { enabled: false, open: '10:00', close: '19:00' },
    sat: { enabled: false, open: '10:00', close: '19:00' },
    sun: { enabled: false, open: '10:00', close: '19:00' },
  }));
  const [error, setError] = useState<string | null>(null);
  const [savedAt, setSavedAt] = useState<Date | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    shopApi.me().then((res) => {
      setShop(res.data);
      if (res.data.opening_hours) {
        setHours((prev) => {
          const next = { ...prev };
          for (const day of WEEKDAYS) {
            const slot = res.data.opening_hours?.[day];
            if (slot) next[day] = { enabled: true, open: slot.open, close: slot.close };
          }
          return next;
        });
      }
    }).catch((err) => setError((err as Error).message));
  }, []);

  function setField<K extends keyof Shop>(key: K, value: Shop[K]) {
    if (!shop) return;
    setShop({ ...shop, [key]: value });
  }

  async function save(e: React.FormEvent) {
    e.preventDefault();
    if (!shop) return;
    setSaving(true);
    setError(null);
    try {
      const openingHoursPayload: OpeningHours = {};
      for (const day of WEEKDAYS) {
        const h = hours[day];
        openingHoursPayload[day] = h.enabled ? { open: h.open, close: h.close } : null;
      }
      const res = await shopApi.update({
        name: shop.name,
        description: shop.description,
        address: shop.address,
        phone: shop.phone,
        min_order_delay_minutes: shop.min_order_delay_minutes,
        opening_hours: openingHoursPayload,
        siret: shop.siret,
        vat_number: shop.vat_number,
        cgv_text: shop.cgv_text,
      });
      setShop(res.data);
      setSavedAt(new Date());
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSaving(false);
    }
  }

  if (!shop) return <p>Chargement…</p>;

  return (
    <>
      <h2>Paramètres boutique</h2>
      {error && <div className="alert alert-error">{error}</div>}
      {savedAt && <div className="alert alert-success">Enregistré à {savedAt.toLocaleTimeString('fr-FR')}</div>}

      <form onSubmit={save}>
        <div className="card">
          <h3 style={{ marginTop: 0 }}>Informations</h3>
          <div className="form-row">
            <label>Nom</label>
            <input required value={shop.name} onChange={(e) => setField('name', e.target.value)} />
          </div>
          <div className="form-row">
            <label>Description</label>
            <textarea rows={4} value={shop.description ?? ''} onChange={(e) => setField('description', e.target.value)} />
          </div>
          <div className="grid-2">
            <div className="form-row">
              <label>Adresse</label>
              <input value={shop.address ?? ''} onChange={(e) => setField('address', e.target.value)} />
            </div>
            <div className="form-row">
              <label>Téléphone</label>
              <input type="tel" value={shop.phone ?? ''} onChange={(e) => setField('phone', e.target.value)} />
            </div>
          </div>
        </div>

        <div className="card">
          <h3 style={{ marginTop: 0 }}>Horaires d’ouverture</h3>
          <p className="muted">Cochez les jours d’ouverture et indiquez les plages horaires. Les créneaux de retrait sont calculés à partir de ces horaires.</p>
          {WEEKDAYS.map((day) => (
            <div key={day} className="form-row-inline" style={{ gap: 12 }}>
              <input
                id={`d-${day}`}
                type="checkbox"
                checked={hours[day].enabled}
                onChange={(e) => setHours({ ...hours, [day]: { ...hours[day], enabled: e.target.checked } })}
                style={{ width: 'auto' }}
              />
              <label htmlFor={`d-${day}`} style={{ width: 90 }}>{WEEKDAY_LABELS[day]}</label>
              <input
                type="time"
                value={hours[day].open}
                disabled={!hours[day].enabled}
                onChange={(e) => setHours({ ...hours, [day]: { ...hours[day], open: e.target.value } })}
                style={{ maxWidth: 140 }}
              />
              <span className="muted">→</span>
              <input
                type="time"
                value={hours[day].close}
                disabled={!hours[day].enabled}
                onChange={(e) => setHours({ ...hours, [day]: { ...hours[day], close: e.target.value } })}
                style={{ maxWidth: 140 }}
              />
            </div>
          ))}
        </div>

        <div className="card">
          <h3 style={{ marginTop: 0 }}>Préparation</h3>
          <div className="form-row">
            <label>Délai minimum de commande (minutes)</label>
            <input
              type="number"
              min={0}
              max={1440}
              value={shop.min_order_delay_minutes}
              onChange={(e) => setField('min_order_delay_minutes', Number(e.target.value))}
              style={{ maxWidth: 200 }}
            />
            <div className="muted">Ex : 30 pour un traiteur, 60 pour une boulangerie, 1440 (24h) pour une paella.</div>
          </div>
        </div>

        <div className="card">
          <h3 style={{ marginTop: 0 }}>Informations légales</h3>
          <div className="grid-2">
            <div className="form-row">
              <label>SIRET</label>
              <input value={shop.siret ?? ''} onChange={(e) => setField('siret', e.target.value)} />
            </div>
            <div className="form-row">
              <label>N° TVA intracom</label>
              <input value={shop.vat_number ?? ''} onChange={(e) => setField('vat_number', e.target.value)} />
            </div>
          </div>
          <div className="form-row">
            <label>Conditions générales de vente (CGV)</label>
            <textarea rows={6} value={shop.cgv_text ?? ''} onChange={(e) => setField('cgv_text', e.target.value)} />
          </div>
        </div>

        <button className="primary" type="submit" disabled={saving}>
          {saving ? 'Enregistrement…' : 'Enregistrer'}
        </button>
      </form>

      <div className="card" style={{ marginTop: 24 }}>
        <h3 style={{ marginTop: 0 }}>Logs d’activité</h3>
        <p className="muted">Exportez l’historique de votre boutique pour vos archives ou joindre à un ticket support.</p>
        <ActivityExport />
      </div>
    </>
  );
}

function ActivityExport() {
  const [period, setPeriod] = useState<'24h' | '7d' | '30d'>('7d');
  return (
    <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
      <select value={period} onChange={(e) => setPeriod(e.target.value as '24h' | '7d' | '30d')} style={{ maxWidth: 160 }}>
        <option value="24h">24 dernières heures</option>
        <option value="7d">7 derniers jours</option>
        <option value="30d">30 derniers jours</option>
      </select>
      <a href={activityExportUrl(period, 'json')} target="_blank" rel="noopener">
        <button>Télécharger JSON</button>
      </a>
      <a href={activityExportUrl(period, 'csv')} target="_blank" rel="noopener">
        <button>Télécharger CSV</button>
      </a>
    </div>
  );
}

function activityExportUrl(period: '24h' | '7d' | '30d', format: 'json' | 'csv'): string {
  const base = import.meta.env.VITE_SHOP_URL ?? 'http://localhost:8789';
  return `${base}/shop/me/activity?period=${period}&format=${format}`;
}
